import { useEffect, useState } from 'react';

import { PublicApiBuild } from '@/models/Build';
import useBuildCacheInsightsApiQuery from '@/pages/BuildCache/BuildCachePageWrapper/pages/Invocations/hooks/useBuildCacheInsights';

import BuildLogSummary from '../../../types/BuildLogSummary';
import { IdentifierOptionsType, ResourceIdentifiersData, ResourceUsageData } from '../ResourceUsageMonitoring.types';
import { getChartData, getSteps } from '../utils/BuildDetailsTabs.utils';

const REFETCH_INTERVAL = 10000;
const REFETCH_WINDOW_AFTER_BUILD_END = 10 * 60 * 1000;

// After a build finishes, we continue to refetch data for 10 minutes to ensure we capture all resource usage data
const refetchIntervalUntil = (until?: number) => () =>
  until === undefined || Date.now() < until ? REFETCH_INTERVAL : false;

export const useResourceUsageData = (
  build: PublicApiBuild,
  isEnabled: boolean,
  traceToken: string,
  summary?: BuildLogSummary,
) => {
  const [step, setStep] = useState<string[]>([]);
  const [select, setSelect] = useState<IdentifierOptionsType>({
    networkInterface: [],
    disk: [],
    mountPath: [],
  });
  const [resourceUsageData, setResourceUsageData] = useState<ResourceUsageData>();

  const [chartLoading, setChartLoading] = useState<Record<string, boolean>>({
    cpu: true,
    memory: true,
    loadAverage: true,
    swap: true,
    networkInterface: true,
    mountPath: true,
    disk: true,
  });

  const buildStart = build.startedOnWorkerAt ? new Date(build.startedOnWorkerAt).getTime() : undefined;
  const buildEnd = build.finishedAt ? new Date(build.finishedAt).getTime() : undefined;

  const refetchInterval = refetchIntervalUntil(
    buildEnd === undefined ? undefined : buildEnd + REFETCH_WINDOW_AFTER_BUILD_END,
  );

  const params = {
    from: buildStart,
    to: buildEnd,
    networkInterface: select?.networkInterface?.[0],
    disk: select?.disk?.[0],
    mountPath: select?.mountPath?.[0],
  };

  const stepBundles: Record<string, { id: string }> = {};
  Object.entries(summary?.execution?.stepBundles || []).forEach(([uuid, { id }]) => {
    const lowerCaseUuid = uuid.toLowerCase();
    stepBundles[lowerCaseUuid] = { id };
  });

  const steps = getSteps(stepBundles, summary);

  const selectedStep = steps.find((s) => s.title === step[0]);
  if (selectedStep) {
    params.from = selectedStep.startedAt;
    params.to = selectedStep.finishedAt;
  }

  const { data: identifiersData, isLoading: isIdentifiersLoading } =
    useBuildCacheInsightsApiQuery<ResourceIdentifiersData>(
      ['resource-usage', build.slug, 'attributes'],
      `/trace/resource-usage/builds/${build.slug}/attributes`,
      traceToken,
      {
        requestConfig: {
          params: { from: params.from, to: params.to },
        },
        enabled: !!isEnabled && !!traceToken && !!buildStart,
        refetchInterval,
        refetchIntervalInBackground: false,
      },
    );

  const {
    data: apiData,
    error: apiDataError,
    isError: isApiDataError,
    isLoading: isApiDataLoading,
    refetch,
  } = useBuildCacheInsightsApiQuery<ResourceUsageData>(
    ['resource-usage', build.slug, 'overview'],
    `/trace/resource-usage/builds/${build.slug}/overview`,
    traceToken,
    {
      requestConfig: {
        params,
      },
      enabled: !!isEnabled && !!traceToken && !!buildStart,
      refetchInterval,
      refetchIntervalInBackground: false,
    },
  );

  const chartDatas = getChartData(resourceUsageData);

  const globalTimestamps = chartDatas.flatMap((chartData) => {
    if (!chartData.data) {
      return [];
    }
    return chartData.data.flatMap((series) => series.dataPoints.map((dataPoint) => dataPoint.timestamp));
  });

  const globalMinTimestamp = globalTimestamps.length > 0 ? Math.min(...globalTimestamps) : undefined;
  const globalMaxTimestamp = globalTimestamps.length > 0 ? Math.max(...globalTimestamps) : undefined;

  useEffect(() => {
    if (apiData) {
      setResourceUsageData(apiData);
    }
  }, [apiData]);

  useEffect(() => {
    if (isEnabled && !isApiDataLoading) {
      setChartLoading({
        cpu: false,
        memory: false,
        loadAverage: false,
        swap: false,
        networkInterface: false,
        mountPath: false,
        disk: false,
      });
    }
  }, [isEnabled, isApiDataLoading, apiData]);

  useEffect(() => {
    if (!!identifiersData && !isIdentifiersLoading) {
      const newValue: IdentifierOptionsType = {};

      if (identifiersData.network_interfaces?.length && !select.networkInterface?.length) {
        newValue.networkInterface = [identifiersData.network_interfaces[0]];
      } else if (!identifiersData.network_interfaces?.length) {
        newValue.networkInterface = [];
      }

      if (identifiersData.mount_paths?.length && !select.mountPath?.length) {
        newValue.mountPath = [identifiersData.mount_paths[0]];
      } else if (!identifiersData.mount_paths?.length) {
        newValue.mountPath = [];
      }

      if (identifiersData.disks?.length && !select.disk?.length) {
        newValue.disk = [identifiersData.disks[0]];
      } else if (!identifiersData.disks?.length) {
        newValue.disk = [];
      }

      if (Object.keys(newValue).length > 0) {
        setSelect((prev) => ({ ...prev, ...newValue }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifiersData, isIdentifiersLoading]);

  return {
    step,
    setStep,
    steps,
    select,
    setSelect,
    chartLoading,
    setChartLoading,
    chartDatas,
    identifiersData,
    isIdentifiersLoading,
    apiDataError,
    isApiDataError,
    isApiDataLoading,
    refetch,
    globalMinTimestamp,
    globalMaxTimestamp,
  };
};
